import axios, { InternalAxiosRequestConfig } from "axios";

import { BASE_URL, BFF_API_KEY } from "@/constants";
import { DEFAULT_FALLBACK_LOCALE } from "@/libs/i18n/config";

const baseURL = BASE_URL;

// Internal locale cache for client-side requests
// Initialized with default to prevent race conditions on first render
let cachedLocale: string = DEFAULT_FALLBACK_LOCALE;
let cachedBackendLocaleCode: string = DEFAULT_FALLBACK_LOCALE;

/**
 * Set locale for axios interceptor (called from client components)
 * This allows us to use HttpOnly cookies while still having locale for API calls
 */
export function setAxiosLocale(
  locale: string,
  backendLocaleCode: string
): void {
  cachedLocale = locale;
  cachedBackendLocaleCode = backendLocaleCode;
}

/**
 * Get cached locale (for use in non-React contexts)
 * Returns the locale set by AxiosLocaleInitializer, or the default if not yet initialized
 */
export function getCachedAxiosLocale(): string {
  return cachedLocale;
}

/**
 * Get cached backend locale code (for Accept-Language header)
 * Returns the backend locale code set by AxiosLocaleInitializer, or the default if not yet initialized
 */
export function getCachedBackendLocaleCode(): string {
  return cachedBackendLocaleCode;
}

const axioInstance = axios.create({
  baseURL,
  headers: {
    "Content-Type": "application/json",
    "x-api-key": `${BFF_API_KEY}`,
  },
  withCredentials: true,
});

axioInstance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  // Use cached backend locale code for Accept-Language header
  config.headers["Accept-Language"] = cachedBackendLocaleCode;
  return config;
});

axioInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response ? error.response.status : null;
    if (status === 401) {
      // Handle unauthorized access
    } else if (status === 404) {
      // Handle not found errors
    }

    // Handle other errors
    return Promise.reject(error);
  }
);

export default axioInstance;
